<template>
  <div>
    <!-- When Selection has entries, render a dropdown with custom entry support -->
    <QAutoComplete
      v-if="hasSelection"
      v-model="localValue"
      :label="label"
      :hint="hint"
      :persistentHint="!!hint"
      :list="combinedItems"
      textkey="text"
      valuekey="value"
      :rules="rules"
      :clearable="!definition.IsRequired"
      externaledit
    >
      <template #append-item>
        <v-divider class="mt-1 mb-1" />
        <v-list-item class="px-2">
          <QTextField
            v-model="newCustomItem"
            :label="'Add custom value'"
            dense
            hide-details
            outlined
            class="mr-2"
            externaledit
            @keydown.enter.prevent="addCustomItem"
          />
          <v-btn icon small color="primary" :disabled="!newCustomItem || !newCustomItem.trim()" @click="addCustomItem">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </v-list-item>
      </template>
    </QAutoComplete>

    <!-- When no Selection, render a free-form string list manager -->
    <div v-else>
      <div class="mb-1">{{ label }}</div>
      <div v-if="hint" class="text-caption grey--text mb-1">{{ hint }}</div>

      <div class="d-flex align-center mb-2">
        <QTextField
          v-model="newCustomItem"
          :label="'Add new item'"
          dense
          hide-details
          outlined
          class="mr-2"
          :externaledit="false"
          @keydown.enter.prevent="addFreeformItem"
        />
        <v-btn icon color="primary" :disabled="!newCustomItem || !newCustomItem.trim()" @click="addFreeformItem">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>

      <div class="d-flex flex-wrap ga-1">
        <v-chip
          v-for="(item, index) in listItems"
          :key="index"
          close
          small
          color="primary"
          dark
          class="ma-1"
          @click:close="removeFreeformItem(index)"
        >
          {{ item }}
        </v-chip>
      </div>

      <div v-if="listItems.length === 0" class="text-caption grey--text mt-1">
        No items added yet
      </div>

      <div v-if="definition.IsRequired && listItems.length === 0" class="red--text text-caption mt-1">
        {{ definition.Name }} is required
      </div>
    </div>
  </div>
</template>

<script>
import QAutoComplete from "@/components/Inputs/QAutoComplete.vue";
import QTextField from "@/components/Inputs/QTextField.vue";

export default {
  name: "QAIFieldStringList",
  components: { QAutoComplete, QTextField },
  props: {
    value: { type: [String, Array], default: "" },
    definition: { type: Object, required: true },
  },

  data() {
    return {
      newCustomItem: "",
      customItems: [],
    };
  },

  computed: {
    hasSelection() {
      return this.definition.Selection && Object.keys(this.definition.Selection).length > 0;
    },
    selectionItems() {
      if (!this.definition.Selection) return [];
      return Object.entries(this.definition.Selection).map(([key, val]) => ({
        value: key,
        text: val,
      }));
    },
    /** Merge predefined selection items with user-added custom items */
    combinedItems() {
      const existing = this.selectionItems;
      const existingValues = new Set(existing.map(i => i.value));
      const custom = this.customItems
        .filter(c => !existingValues.has(c))
        .map(c => ({ value: c, text: c }));
      return [...existing, ...custom];
    },
    localValue: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    /** Items list for the free-form (no-selection) mode */
    listItems() {
      if (Array.isArray(this.value)) return this.value;
      if (this.value) return [this.value];
      return [];
    },
    label() {
      return this.definition.Description || this.definition.Name;
    },
    hint() {
      return this.definition.DefaultValue ? `Default: ${this.definition.DefaultValue}` : "";
    },
    rules() {
      return this.definition.IsRequired ? [v => !!v || `${this.definition.Name} is required`] : [];
    },
  },

  methods: {
    /** Add a custom item to the dropdown list (Selection mode) */
    addCustomItem() {
      const val = (this.newCustomItem || "").trim();
      if (!val) return;
      if (!this.customItems.includes(val)) {
        this.customItems.push(val);
      }
      // Auto-select the newly added custom item
      this.localValue = val;
      this.newCustomItem = "";
    },
    /** Add an item to the free-form string list (no Selection mode) */
    addFreeformItem() {
      const val = (this.newCustomItem || "").trim();
      if (!val) return;
      const current = Array.isArray(this.value) ? [...this.value] : this.value ? [this.value] : [];
      if (!current.includes(val)) {
        current.push(val);
      }
      this.$emit("input", current);
      this.newCustomItem = "";
    },
    /** Remove an item from the free-form string list by index */
    removeFreeformItem(index) {
      const current = Array.isArray(this.value) ? [...this.value] : this.value ? [this.value] : [];
      current.splice(index, 1);
      this.$emit("input", current);
    },
  },
};
</script>
